import { useRouter } from '@application/utils';
import type { BreedComparatorGetBreedsQuery } from '@gql/graphql';
import { DogBreedImgType } from '@gql/graphql';
import { IconScalePaws } from '@presentation/components';
import { PLACEHOLDER_IMAGE_DOG } from '@presentation/constants';
import type { BreedComparatorData } from '@presentation/pages';
import { useTranslation } from '@presentation/utils';

export const useFormatBreedsForComparison = (
  breeds: BreedComparatorGetBreedsQuery['findManyDogBreed'],
) => {
  const { language } = useRouter();
  const { translate } = useTranslation();

  const formatBreedsForComparison = (): BreedComparatorData | undefined => {
    if (!breeds || breeds.length === 0) {
      return;
    }

    return {
      general: breeds.map((breed) => ({
        id: breed.id,
        name: breed.name,
        img:
          breed.imgs.find((img) => img.type === DogBreedImgType.MAIN)?.small ??
          PLACEHOLDER_IMAGE_DOG,
      })),
      scores: [
        {
          nodes: [
            {
              label: translate('glossary.otherNames'),
              values: breeds?.map((breed) => {
                const translation = breed?.translations?.find((x) => x.language === language);
                return translation?.otherNames.length ? translation?.otherNames.join(', ') : '-';
              }),
            },
            {
              label: translate('glossary.fciGroup'),
              values: breeds?.map((breed) => {
                return breed.fciGroup ? translate(`enum.breedFciGroupType.${breed.fciGroup}`) : '-';
              }),
            },
            {
              label: translate('glossary.hair'),
              values: breeds?.map((breed) => {
                return breed.hairType
                  ? translate(`enum.hairType.${breed.hairType.replace('_', '')}`)
                  : '-';
              }),
            },
            {
              label: translate('glossary.head'),
              values: breeds?.map((breed) => {
                return breed.headShape ? translate(`enum.headShape.${breed.headShape}`) : '-';
              }),
            },
          ],
        },
        {
          label: translate('breedComparator.sections.fragilities'),
          nodes: [
            {
              label: translate('glossary.robustness'),
              values: breeds?.map((breed) => (
                <IconScalePaws key={`robustness-${breed.id}`} amount={breed.robustnessIndex ?? 5} />
              )),
            },
            {
              label: translate('glossary.maintenance'),
              values: breeds?.map((breed) => (
                <IconScalePaws
                  key={`maintenance-${breed.id}`}
                  amount={breed.maintenanceIndex ?? 5}
                />
              )),
            },
            {
              label: translate('glossary.hairLoss'),
              values: breeds?.map((breed) => (
                <IconScalePaws key={`hairLoss-${breed.id}`} amount={breed.hairLossIndex ?? 5} />
              )),
            },
            {
              label: translate('glossary.supportsHeat'),
              values: breeds?.map((breed) => (
                <IconScalePaws
                  key={`supportsHeat-${breed.id}`}
                  amount={breed.heatToleranceIndex ?? 5}
                />
              )),
            },
            {
              label: translate('glossary.supportsCold'),
              values: breeds?.map((breed) => (
                <IconScalePaws
                  key={`supportsCold-${breed.id}`}
                  amount={breed.coldToleranceIndex ?? 5}
                />
              )),
            },
          ],
        },
        {
          label: translate('breedComparator.sections.advices'),
          nodes: [
            {
              label: translate('glossary.predatoryInstinct'),
              values: breeds?.map((breed) => (
                <IconScalePaws
                  key={`robustness-${breed.id}`}
                  amount={breed.predatoryInstinctIndex ?? 5}
                />
              )),
            },
            {
              label: translate('glossary.independent'),
              values: breeds?.map((breed) => (
                <IconScalePaws
                  key={`maintenance-${breed.id}`}
                  amount={breed.independenceIndex ?? 5}
                />
              )),
            },
            {
              label: translate('glossary.barkingTendency'),
              values: breeds?.map((breed) => (
                <IconScalePaws
                  key={`hairLoss-${breed.id}`}
                  amount={breed.barkingTendancyIndex ?? 5}
                />
              )),
            },
            {
              label: translate('glossary.foreignersSociability'),
              values: breeds?.map((breed) => (
                <IconScalePaws
                  key={`supportsHeat-${breed.id}`}
                  amount={breed.foreignerSociabilityIndex ?? 5}
                />
              )),
            },
            {
              label: translate('glossary.needPhysicalActivities'),
              values: breeds?.map((breed) => (
                <IconScalePaws
                  key={`supportsCold-${breed.id}`}
                  amount={breed.physicalActivityNeedIndex ?? 5}
                />
              )),
            },
          ],
        },
      ],
      overview: breeds?.map((breed) => {
        const translation = breed?.translations?.find((x) => x.language === language);
        return translation?.description ?? '';
      }),
      historic: breeds?.map((breed) => {
        const translation = breed?.translations?.find((x) => x.language === language);
        return translation?.historic ?? '';
      }),
      morphology: breeds?.map((breed) => {
        const translation = breed?.translations?.find((x) => x.language === language);
        return translation?.morphology ?? '';
      }),
      fragilities: breeds?.map((breed) => {
        const translation = breed?.translations?.find((x) => x.language === language);
        return translation?.fragilities ?? '';
      }),
      advices: breeds?.map((breed) => {
        const translation = breed?.translations?.find((x) => x.language === language);
        return translation?.advices ?? '';
      }),
    };
  };

  return { formattedBreeds: formatBreedsForComparison() };
};
